import { Injectable } from '@angular/core';
import { StorageService } from '@ridango/core/lib/services/storage.service';
import { AddressResult, StopRouteResult, PoiAddressResult } from '../search/search.model';
import { Observable } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

type SearchResultItems = AddressResult | StopRouteResult | PoiAddressResult;
const DEFAULT_BUS_STOPS_PREVIOUS_SEARCHES_LIMIT = 3;

@Injectable({
    providedIn: 'root'
})
export class OtpRecentSearchUtilService {
    private readonly RECENT_SEARCH_ITEMS_STORAGE_KEY = 'openTripPlannerRecentSearchItems';
    private readonly CACHED_SEARCH_ITEMS_STORAGE_KEY = 'openTripPlannerCachedSearchItems';

    constructor(private storageService: StorageService) {}

    addToRecentSearch(
        item: SearchResultItems,
        key: string,
        limit = DEFAULT_BUS_STOPS_PREVIOUS_SEARCHES_LIMIT
    ): Observable<void> {
        return this.getItems<SearchResultItems>(this.RECENT_SEARCH_ITEMS_STORAGE_KEY, key).pipe(
            switchMap((currentItems) => {
                const updatedItems = this.updateItems(currentItems, item, limit);
                return this.storageService.setItem(`${this.RECENT_SEARCH_ITEMS_STORAGE_KEY}_${key}`, updatedItems);
            })
        );
    }

    addToCachedSearch(item: SearchResultItems, key: string): Observable<void> {
        return this.getItems<SearchResultItems>(this.CACHED_SEARCH_ITEMS_STORAGE_KEY, key).pipe(
            switchMap((currentItems) => {
                const updatedItems = this.updateItems(currentItems, item);
                return this.storageService.setItem(`${this.CACHED_SEARCH_ITEMS_STORAGE_KEY}_${key}`, updatedItems);
            })
        );
    }

    getRecentSearchItems(key: string): Observable<SearchResultItems[]> {
        return this.getItems(this.RECENT_SEARCH_ITEMS_STORAGE_KEY, key);
    }

    getCachedSearchItems(key: string): Observable<SearchResultItems[]> {
        return this.getItems(this.CACHED_SEARCH_ITEMS_STORAGE_KEY, key);
    }

    clearRecentSearchItems(key: string): Observable<void> {
        return this.storageService.removeItem(`${this.RECENT_SEARCH_ITEMS_STORAGE_KEY}_${key}`);
    }

    clearCachedSearchItems(key: string): Observable<void> {
        return this.storageService.removeItem(`${this.CACHED_SEARCH_ITEMS_STORAGE_KEY}_${key}`);
    }

    private getItems<T>(prefix: string, key: string): Observable<T[]> {
        return this.storageService.getItem<T[]>(`${prefix}_${key}`).pipe(map((items) => items || ([] as T[])));
    }

    private updateItems<T extends SearchResultItems>(currentItems: T[], newItem: T, limit?: number): T[] {
        const filteredItems = currentItems.filter((existingItem) => existingItem.id !== newItem.id);
        const updatedItems = [newItem, ...filteredItems];
        return limit ? updatedItems.slice(0, limit) : updatedItems;
    }
}
